'use client';
import { useTranslations } from 'next-intl';
import React, { useMemo } from 'react';

import { SeatType } from '@cineamo/api/models/seat/SeatDto.types';

import { cn } from '@cineamo/ui/src/helpers/cn';

import type { Seat } from '@cineamo/ui/src/seat-map/typedefs';

const byPosition = (a: Seat, b: Seat) =>
    String(a.positionName).localeCompare(String(b.positionName), undefined, { numeric: true });

/**
 * Keyboard and screen-reader path to the seats: the map is a canvas, so it offers neither. Selection goes through
 * the same handler as a click on the map.
 *
 * Seats are grouped by section (seat category), then by row, and each seat is announced by its number alone. The
 * section and row are spoken on entering them: Tab enters a row at its first seat (or its last, backwards), so
 * those two seats carry the context in their label. Not a `fieldset`/`role="group"` label instead: VoiceOver does
 * not announce those when focus moves by Tab.
 */
const SeatList: React.FC<{
    seats: Seat[];
    onToggle: (seat: Seat) => void;
    isDisabled?: boolean;
    className?: string;
}> = ({ seats, onToggle, isDisabled, className }) => {
    const t = useTranslations('tickets');

    const sections = useMemo(() => {
        const bySection = new Map<string, Map<string, Seat[]>>();
        seats.forEach((seat) => {
            const rows = bySection.get(seat.category?.name ?? '') ?? new Map<string, Seat[]>();
            rows.set(seat.rowName, [...(rows.get(seat.rowName) ?? []), seat]);
            bySection.set(seat.category?.name ?? '', rows);
        });
        return [...bySection.entries()].map(([name, rows]) => ({
            name,
            rows: [...rows.entries()].map(([rowName, rowSeats]) => ({ rowName, seats: rowSeats.sort(byPosition) }))
        }));
    }, [seats]);

    return (
        <div className={cn('w-full space-y-16 overflow-y-auto', className)}>
            {sections.map(({ name, rows }) => (
                <div
                    key={name}
                    className="space-y-4">
                    {name && <h3 className="pb-4 text-body-bold-14">{name}</h3>}
                    {rows.map(({ rowName, seats: rowSeats }, rowIndex) => (
                        <div
                            key={rowName}
                            className="flex flex-wrap items-center gap-6 py-4">
                            {/* A row named like its section (e.g. "Doppelsitze") would only repeat the heading. */}
                            {rowName !== name && (
                                <span
                                    aria-hidden
                                    className="min-w-48 text-body-bold-12">
                                    {rowName}
                                </span>
                            )}
                            {rowSeats.map((seat, seatIndex) => {
                                const isFirst = seatIndex === 0;
                                const isLast = seatIndex === rowSeats.length - 1;
                                // Tab enters a section at its first row's first seat, or backwards at its last row's last.
                                const entersSection =
                                    (isFirst && rowIndex === 0) || (isLast && rowIndex === rows.length - 1);
                                const isWheelchair = [SeatType.WHEELCHAIR, SeatType.DISABLED].includes(seat.type);
                                const unavailable = isWheelchair || !!seat.isLocked;
                                const detail = isWheelchair
                                    ? t('seat-wheelchair-accessible')
                                    : seat.isLocked
                                      ? t('seat-unavailable')
                                      : null;
                                return (
                                    <button
                                        key={seat.id}
                                        type="button"
                                        aria-pressed={!!seat.isSelected}
                                        disabled={isDisabled || unavailable || !!seat.isProcessing}
                                        aria-label={[
                                            entersSection && name,
                                            (isFirst || isLast) &&
                                                rowName !== name &&
                                                t('seat-list-row', { row: rowName }),
                                            seat.positionName,
                                            detail
                                        ]
                                            .filter(Boolean)
                                            .join(', ')}
                                        onClick={() => onToggle({ ...seat, isSelected: !seat.isSelected })}
                                        className={cn(
                                            'min-w-32 rounded-4 border border-current px-6 py-4 text-body-12 outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-color)] disabled:cursor-not-allowed disabled:opacity-40',
                                            seat.isSelected &&
                                                'bg-[var(--primary-color)] text-[var(--on-primary-color)]'
                                        )}>
                                        {seat.positionName}
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

export default SeatList;
